import {Injectable, InjectionToken, Injector} from "@angular/core";
import {DisplayService} from "@app/presentation/services/display.service";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {routesNames} from "@app/app-routing.module";
import {
    DeleteDeliveryPointComponent
} from "@app/presentation/pages/delete-delivery-point/delete-delivery-point.component";
import {take} from "rxjs/operators";
import {SideSheetService} from "@app/presentation/services/side-sheet/side-sheet.service";
import {DeliveryPoint} from "@app/core/delivery-point/application/dtos/delivery-point";
import {ComponentType, Overlay, OverlayRef} from "@angular/cdk/overlay";
import {
    SelectDeliveryPointComponent
} from "@app/presentation/pages/select-delivery-point/select-delivery-point.component";
import {ComponentPortal} from "@angular/cdk/portal";
import {firstValueFrom} from 'rxjs';

type CloseFunctionType = (deliveryPoint?: DeliveryPoint) => void;

@Injectable()
export class SelectDeliveryPointRef {
    private closeFn: CloseFunctionType = (_deliveryPoint) => {/*void*/};

    onClose(closeFn: CloseFunctionType) {
        this.closeFn = closeFn;
    }

    close(deliveryPoint?: DeliveryPoint) {
        this.closeFn(deliveryPoint);
    }
}

export const SELECT_DELIVERY_POINT_DATA = new InjectionToken<{ deliveryPoints: DeliveryPoint[]}>('SELECT_DELIVERY_POINT_DATA');

@Injectable()
export class DeliveryPointService {

    routesNames = routesNames;

    constructor(
        private displayService: DisplayService,
        private bottomSheet: MatBottomSheet,
        private sideSheetService: SideSheetService,
        private overlay: Overlay,
    ) {
    }

    openDeleteDeliveryPoint(deliveryPointCode: number) {
        this.displayService.isMobileDisplay$.pipe(take(1)).subscribe({
            next: isMobile => {
                if (isMobile) {
                    this.bottomSheet.open(DeleteDeliveryPointComponent, {
                        panelClass: 'ep-bottom-sheet',
                        data: {
                            deliveryPointCode: deliveryPointCode
                        }
                    });
                } else {
                    this.sideSheetService.open(DeleteDeliveryPointComponent,{
                        data: {
                            deliveryPointCode: deliveryPointCode
                        }
                    });
                }
            }
        })
    }

    async selectDeliveryPoint(deliveryPoints: DeliveryPoint[], shouldCloseWhenSelectionIsConfirmed: boolean = true): Promise<DeliveryPoint | null> {
        const isMobile = await firstValueFrom(this.displayService.isMobileDisplay$.pipe(take(1)));
        if (isMobile) {
            return new Promise<DeliveryPoint | null>((resolve) => {
                const {selectDeliveryPointRef, close } = this.openFullScreenOverlay(SelectDeliveryPointComponent, deliveryPoints);
                selectDeliveryPointRef.onClose(deliveryPoint => {
                    close();
                    resolve(deliveryPoint ?? null)
                });
            });
        } else {
            const sideSheetRef = this.sideSheetService.open(SelectDeliveryPointComponent,{
                data: {
                    deliveryPoints: deliveryPoints,
                    closeWhenSelectDeliveryPoint: shouldCloseWhenSelectionIsConfirmed,
                }
            })
            return firstValueFrom(sideSheetRef.afterClosedWithValue().pipe(take(1)));
        }
    }

    private openFullScreenOverlay(component: ComponentType<any>, deliveryPoints: DeliveryPoint[]) {
        const overlyRef = this.overlay.create({
            height: '100%',
            width: '100%',
            disposeOnNavigation: true,
        });
        const injector = Injector.create({
            providers: [
                { provide: OverlayRef, useValue: overlyRef },
                { provide: SELECT_DELIVERY_POINT_DATA, useValue: {deliveryPoints,closeWhenSelectDeliveryPoint: true} },
                { provide: SelectDeliveryPointRef },
            ]
        });
        
        const selectDeliveryPointRef = injector.get(SelectDeliveryPointRef);
        firstValueFrom(overlyRef.detachments()).then(() => selectDeliveryPointRef.close())
        const portal = new ComponentPortal(component, undefined, injector);
        overlyRef.attach(portal);
        return {
            selectDeliveryPointRef: selectDeliveryPointRef,
            close: () => overlyRef.dispose(),
        }
    }
}
